import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import ArrowIcon from "../../../Common/Svgs/ArrowIcon";
import TickIcon from "../../../Common/Svgs/TickIcon";
import CustomModal from "../../../Core/Components/Modal/CustomModal";
import { getTrikeByIdV3 } from "../../../Redux/APIs/trikesAPI";
import useRule from "../../../Core/hooks/useRule";
import { TENANT_RULE_VALUE } from "../../Login/constants";

const IndexPassIcon = () => {
  return (
    <div className="w-6 h-6 rounded-full flex items-center justify-center bg-green">
      <TickIcon size={14} className="text-yellow" />
    </div>
  );
};

const IndexFailIcon = () => {
  return (
    <div className="w-6 h-6 rounded-full flex items-center justify-center bg-alert-red">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        fill="none"
        viewBox="0 0 14 14"
      >
        <path
          stroke="#FAFAFA"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7 12.833c3.208 0 5.833-2.625 5.833-5.833S10.208 1.167 7 1.167 1.167 3.792 1.167 7 3.792 12.833 7 12.833M7 4.667v2.916M6.997 9.333h.005"
        />
      </svg>
    </div>
  );
};

const SerialIcon = ({ id }) => {
  return (
    <div className="w-6 h-6 rounded-full flex items-center justify-center bg-[#E7E7E7]">
      <p className="font-poppins text-sm text-dark-gray">{id}</p>
    </div>
  );
};

const SafetyChecklistModal = ({ visible, onCancel, data = [] }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { checklistModal } = useSelector((state) => state.rentals);

  const hasKnownIssueAccess = useRule([TENANT_RULE_VALUE.KNOWN_ISSUES]);

  const mandatory = data?.filter((item) => item?.isMandatory);
  const optional = data?.filter((item) => !item?.isMandatory);

  const renderOptionalIcon = ({ passed, failed, id }) => {
    if (passed) {
      return <IndexPassIcon />;
    }
    if (failed) {
      return <IndexFailIcon />;
    }
    return <SerialIcon id={id} />;
  };

  const handleKnownIssues = async () => {
    const trikeId = checklistModal?.data?.trikeId || 0;
    const registrationNo = checklistModal?.data?.trikeRegistrationNo || "";

    await dispatch(getTrikeByIdV3({ id: trikeId }));

    if (registrationNo) navigate(`/assets/trike-known-issue/${registrationNo}`);

    onCancel();
  };

  return (
    <CustomModal visible={visible} onCancel={onCancel} width="662px">
      <div
        className="p-6 max-h-[685px] overflow-y-scroll"
        style={{
          boxShadow:
            "12px 12px 16px -4px rgba(16, 24, 40, 0.08), -4px -4px 6px -2px rgba(16, 24, 40, 0.03)",
        }}
      >
        <div className="flex items-center justify-between">
          <p className="font-poppins text-base font-medium">
            Safety Checklist Form
          </p>

          <button type="button" onClick={onCancel}>
            <CloseIcon className="stroke-dark-gray w-6 h-6" />
          </button>
        </div>

        <p className="font-aileron text-sm font-normal text-[#6F6F6F]">
          View the status of the vehicle recorded at the hub station.
        </p>

        {hasKnownIssueAccess && (
          <button
            type="button"
            className="mt-2 py-2 px-4 rounded-md bg-primary-10 text-primary"
            onClick={handleKnownIssues}
          >
            <div className="flex items-center gap-x-2">
              <p className="font-poppins text-sm font-medium">Known Issues</p>
              <ArrowIcon size={20} className="rotate-90" strokeWidth={1} />
            </div>
          </button>
        )}

        <div className="my-6">
          {mandatory?.map((item, index) => {
            return (
              <div
                key={item.id}
                className={`flex items-center justify-between py-3 border-b border-[#E7E7E7] ${
                  index === 0 && "border-t"
                }`}
              >
                <div className="flex items-center gap-x-2">
                  <div className="flex-1">
                    <IndexPassIcon />
                  </div>
                  <p className="font-poppins text-sm font-medium capitalize">
                    {item.partName}*
                  </p>
                </div>

                <div className="flex items-center gap-x-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#E7E7E7]">
                    <CloseIcon className="w-4 h-4 stroke-[#6F6F6F]" />
                  </div>

                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-green">
                    <TickIcon size={16} className="text-white" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center font-poppins text-sm font-medium">
          Optional Checks
        </p>

        <div className="my-6">
          {optional?.map((item, index) => {
            const passed = item?.passed === 1;
            const failed = item?.passed === 0;

            const closeIconBg = failed ? "bg-alert-red" : "bg-[#E7E7E7]";
            const closeIconColor = failed ? "stroke-white" : "stroke-[#6F6F6F]";
            const tickIconBg = passed ? "bg-green" : "bg-[#E7E7E7]";
            const tickIconColor = passed ? "text-white" : "text-[#6F6F6F]";

            return (
              <div
                key={item.id}
                className={`flex items-center justify-between py-3 border-b border-[#E7E7E7] ${
                  index === 0 && "border-t"
                }`}
              >
                <div className="flex items-center gap-x-2">
                  <div className="flex-1">
                    {renderOptionalIcon({ passed, failed, id: index + 1 })}
                  </div>

                  <p className="font-poppins text-sm font-medium capitalize">
                    {item.partName}
                  </p>
                </div>

                <div className="flex items-center gap-x-6">
                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${closeIconBg}`}
                  >
                    <CloseIcon className={`w-4 h-4 ${closeIconColor}`} />
                  </div>

                  <div
                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${tickIconBg}`}
                  >
                    <TickIcon size={16} className={` ${tickIconColor}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </CustomModal>
  );
};

export default SafetyChecklistModal;
